/**
 * Product-analytics event taxonomy shared by every frontend (native client +
 * web mini-apps + landing + auth-web). Keep names stable and dot-namespaced;
 * server-authoritative events (signed_up, match.created, message.sent …) are
 * emitted from the backend — the client emits screen views, intents, and UI
 * interactions. Together they give full-funnel coverage.
 */
export const TelemetryEvent = {
  // App lifecycle
  APP_OPENED: "app.opened",
  SCREEN_VIEWED: "$screen",

  // Auth / signup funnel (client intent — server confirms with signed_up)
  AUTH_STARTED: "auth.started",
  AUTH_METHOD_SELECTED: "auth.method_selected",
  OTP_REQUESTED: "auth.otp_requested",
  OTP_SUBMITTED: "auth.otp_submitted",
  SIGNED_IN: "signed_in",
  SIGNED_OUT: "signed_out",

  // Onboarding
  ONBOARDING_STEP_VIEWED: "onboarding.step_viewed",
  ONBOARDING_STEP_SUBMITTED: "onboarding.step_submitted",
  ONBOARDING_COMPLETED: "onboarding.completed",
  LOCATION_PERMISSION_PROMPTED: "location.permission_prompted",
  LOCATION_SET: "location.set",

  // Profile
  PROFILE_EDIT_OPENED: "profile.edit_opened",
  PROFILE_UPDATED: "profile.updated",
  PHOTO_UPLOAD_STARTED: "photo.upload_started",
  PHOTO_UPLOADED: "photo.uploaded",
  TAGS_UPDATED: "tags.updated",

  // Matching / deck
  DECK_VIEWED: "deck.viewed",
  PROFILE_OPENED: "profile.opened",
  SWIPE_LIKE: "swipe.like",
  SWIPE_PASS: "swipe.pass",
  SWIPE_SUPER_LIKE: "swipe.super_like",
  MATCH_POPUP_SHOWN: "match.popup_shown",

  // Chat
  CHAT_LIST_VIEWED: "chat.list_viewed",
  CHAT_OPENED: "chat.opened",
  MESSAGE_SENT: "message.sent",
  MESSAGE_REACTION_ADDED: "message.reaction_added",

  // Events
  EVENTS_LIST_VIEWED: "events.list_viewed",
  EVENT_OPENED: "event.opened",
  EVENT_TICKET_PURCHASE_STARTED: "event.ticket_purchase_started",
  EVENT_TICKET_PURCHASED: "event.ticket_purchased",
  EVENT_CHECK_IN_SCANNED: "event.check_in_scanned",
  EVENT_TICKET_RELEASED: "event.ticket_released",

  // Meetup / location
  MEETUP_CARD_VIEWED: "meetup.card_viewed",
  MEETUP_PICKER_OPENED: "meetup.picker_opened",
  MEETUP_SPOT_SELECTED: "meetup.spot_selected",
  MEETUP_SPOT_PROPOSED: "meetup.spot_proposed",
  MEETUP_CONFIRMED: "meetup.confirmed",
  MEETUP_BOOKING_DISSOLVED: "meetup.booking_dissolved",

  // Connect / QR
  CONNECT_QR_SHOWN: "connect.qr_shown",
  CONNECT_QR_SCANNED: "connect.qr_scanned",
  CONNECT_REQUEST_SENT: "connect.request_sent",
  CONNECT_REQUEST_ACCEPTED: "connect.request_accepted",

  // Gamification / points / rewards
  POINTS_SCREEN_VIEWED: "points.screen_viewed",
  REWARD_CLAIM_STARTED: "reward.claim_started",
  REWARD_CLAIMED: "reward.claimed",
  LEADERBOARD_VIEWED: "leaderboard.viewed",
  PHOTO_CHALLENGE_SUBMITTED: "photo_challenge.submitted",
  MINI_GAME_STARTED: "mini_game.started",
  MINI_GAME_FINISHED: "mini_game.finished",

  // Mini-apps
  MINI_APP_OPENED: "mini_app.opened",
  MINI_APP_ACTION: "mini_app.action",

  // Business / company mini-app
  COMPANY_CREATED: "company.created",
  COMPANY_UPDATED: "company.updated",
  COMPANY_MEMBER_INVITED: "company.member_invited",
  COMPANY_MEMBER_ROLE_UPDATED: "company.member_role_updated",
  COMPANY_MEMBER_DISMISSED: "company.member_dismissed",

  // Marketing (landing)
  WAITLIST_FORM_VIEWED: "waitlist.form_viewed",
  WAITLIST_SUBMITTED: "waitlist.submitted",
  CTA_CLICKED: "cta.clicked",
} as const;

export type TelemetryEventName =
  (typeof TelemetryEvent)[keyof typeof TelemetryEvent];
